// components/portal/useTableColumns.ts
'use client';

import { useCallback, useMemo, useRef, useState } from 'react';

/** Minimal shape a column needs to be sized and ordered. */
export interface SizableColumn {
  id: string;
  minWidth: number;
}

const MIN_COLUMN_WIDTH = 60;
const MAX_AUTO_SIZE_WIDTH = 800;

/**
 * Column widths and left/right ordering for a table header.
 *
 * Extracted from Table.tsx, which already owned the resize half of this, so the two do not drift.
 * Widths live in a ref with a re-render tick rather than in state: a resize fires on every
 * mousemove, and Table.tsx renders hundreds of rows, so storing them in state would re-render the
 * whole grid on each frame. Order is ordinary state, since it only changes on drop.
 *
 * Deliberately not persisted, matching the previous behaviour: a reload restores the defaults.
 */
export function useTableColumnSizing() {
  const widthsRef = useRef<Record<string, number>>({});
  /**
   * Bumped on every width change. Consumers that memoise a grid template MUST depend on this:
   * the widths themselves live in a ref, so nothing else changes identity when a column resizes
   * and the memo would serve a stale template mid-drag.
   */
  const [widthVersion, setWidthTick] = useState(0);

  /**
   * @param scale Multiplier turning a pixel delta into a template-unit delta. Widths feed an
   *   `fr` template, so a raw pixel delta makes the grip drift behind the cursor. Callers pass
   *   totalUnits / containerPixelWidth; 1 keeps the previous pixel-for-pixel behaviour.
   */
  const startResize = useCallback((columnId: string, startX: number, startWidth: number, scale = 1) => {
    const onMove = (event: MouseEvent) => {
      widthsRef.current = {
        ...widthsRef.current,
        [columnId]: Math.max(MIN_COLUMN_WIDTH, startWidth + (event.clientX - startX) * scale),
      };
      setWidthTick((tick) => tick + 1);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  /**
   * Widens a column to fit its widest rendered cell, measured from the live DOM.
   *
   * The grid element has to be resolved rather than assumed. Table.tsx marks the row and wraps
   * its grid one level down; Activity puts the mark on the grid row itself. Blindly taking
   * `row.firstElementChild` therefore read a CELL as the grid on one surface (so every lookup
   * missed and the width collapsed to the 80 floor) and the grid as a cell at index 0 on the
   * other (so repeated double-clicks measured the grid's own width and grew without bound).
   * Matching on child count identifies the grid on both.
   */
  const autoSizeAt = useCallback(
    (columnId: string, container: HTMLElement | null, columnIndex: number, columnCount: number) => {
      if (!container || columnIndex < 0 || columnCount <= 0) return;
      const rows = container.querySelectorAll<HTMLElement>('[data-table-row-id]');
      let widest = 0;
      rows.forEach((row) => {
        const grid =
          row.children.length === columnCount
            ? row
            : ((row.firstElementChild as HTMLElement | null)?.children.length === columnCount
                ? (row.firstElementChild as HTMLElement)
                : null);
        if (!grid) return;
        const cell = grid.children[columnIndex] as HTMLElement | undefined;
        if (!cell) return;
        const target = (cell.firstElementChild as HTMLElement | null) ?? cell;
        const width = target.scrollWidth + 32;
        if (width > widest) widest = width;
      });
      // Nothing measured means the DOM shape did not match. Leave the column alone rather than
      // snapping it to a floor that looks like a real result.
      if (widest === 0) return;
      widthsRef.current = {
        ...widthsRef.current,
        [columnId]: Math.min(Math.max(widest, MIN_COLUMN_WIDTH), MAX_AUTO_SIZE_WIDTH),
      };
      setWidthTick((tick) => tick + 1);
    },
    []
  );

  return { widthsRef, widthVersion, startResize, autoSizeAt };
}

export function useTableColumns<T extends SizableColumn>(columns: T[]) {
  const { widthsRef, widthVersion, startResize, autoSizeAt } = useTableColumnSizing();
  const [order, setOrder] = useState<string[] | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  /** Columns in the user's order. Unknown ids (a column added since the drag) keep their position. */
  const orderedColumns = useMemo(() => {
    if (!order) return columns;
    const byId = new Map(columns.map((column) => [column.id, column]));
    const ordered = order.map((id) => byId.get(id)).filter((column): column is T => Boolean(column));
    const missing = columns.filter((column) => !order.includes(column.id));
    return [...ordered, ...missing];
  }, [columns, order]);

  const getWidth = useCallback(
    (column: T) => widthsRef.current[column.id] ?? column.minWidth,
    [widthsRef]
  );

  const moveColumn = useCallback(
    (fromId: string, toId: string) => {
      if (fromId === toId) return;
      setOrder((previous) => {
        const current = previous ?? columns.map((column) => column.id);
        const fromIndex = current.indexOf(fromId);
        const toIndex = current.indexOf(toId);
        if (fromIndex < 0 || toIndex < 0) return current;
        const next = [...current];
        next.splice(fromIndex, 1);
        next.splice(toIndex, 0, fromId);
        return next;
      });
    },
    [columns]
  );

  /** Spread onto a header cell to make it a drag handle and a drop target. */
  const getReorderProps = useCallback(
    (columnId: string) => ({
      draggable: true,
      onDragStart: (event: React.DragEvent) => {
        setDraggingId(columnId);
        event.dataTransfer.effectAllowed = 'move';
        // Firefox ignores a drag that carries no payload.
        event.dataTransfer.setData('text/plain', columnId);
      },
      // The highlight hangs off dragenter, not dragover: the app shell's global file-drop
      // handler registers a bubble-phase dragover that calls stopPropagation, and React's
      // listener sits above it, so a synthetic onDragOver inside the shell never runs at all.
      // dragenter and dragleave are not intercepted.
      onDragEnter: (event: React.DragEvent) => {
        if (!draggingId || draggingId === columnId) return;
        event.preventDefault();
        setDropTargetId(columnId);
      },
      onDragOver: (event: React.DragEvent) => {
        // Dead inside the app shell (see above), kept so the hook still works on any surface
        // that does not install that global handler.
        if (!draggingId || draggingId === columnId) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTargetId(columnId);
      },
      onDragLeave: () => setDropTargetId((current) => (current === columnId ? null : current)),
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        const fromId = draggingId ?? event.dataTransfer.getData('text/plain');
        if (fromId) moveColumn(fromId, columnId);
        setDraggingId(null);
        setDropTargetId(null);
      },
      onDragEnd: () => {
        setDraggingId(null);
        setDropTargetId(null);
      },
    }),
    [draggingId, moveColumn]
  );

  return {
    orderedColumns,
    getWidth,
    widthVersion,
    startResize,
    autoSize: autoSizeAt,
    getReorderProps,
    draggingId,
    dropTargetId,
  };
}
